import {
  Activity,
  BadgeAlert,
  BoxSelect,
  CircleDot,
  Download,
  FileUp,
  Image,
  Layers,
  Maximize2,
  MousePointer2,
  ScanSearch,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Wand2,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import {
  PointerEvent,
  WheelEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  analyzeBox,
  analyzePoint,
  buildLocalAiSummary,
  detectHotspots,
  severityLabel
} from "./lib/analysis";
import { paletteOptions } from "./lib/palettes";
import { drawHeatmap, exportAnnotatedPng, renderHeatmapDataUrl } from "./lib/render";
import {
  applyRadiometricCorrection,
  createSyntheticThermogram,
  decodeModeLabel,
  decodeThermogramFile,
  formatTemp,
  valueStats
} from "./lib/thermogram";
import type {
  AiRequestPayload,
  AnalysisBox,
  AnalysisPoint,
  PaletteId,
  RadiometricSettings,
  ThermalRange,
  Thermogram,
  ToolMode,
  ZoneMetrics
} from "./lib/types";

const initialSettings: RadiometricSettings = {
  emissivity: 0.95,
  reflectedTempC: 20,
  ambientTempC: 22,
  distanceM: 1.5,
  humidityPct: 45
};

const initialBoxes: AnalysisBox[] = [
  { id: "zone-a", label: "Zone A", x: 0.58, y: 0.25, width: 0.2, height: 0.34 },
  { id: "zone-b", label: "Zone B", x: 0.25, y: 0.29, width: 0.18, height: 0.26 }
];

const initialPoints: AnalysisPoint[] = [
  { id: "pt-1", label: "P1", x: 0.67, y: 0.39 }
];

const toolLabels: Record<ToolMode, string> = {
  box: "Box",
  point: "Point",
  pan: "Select"
};

type DraftBox = {
  startX: number;
  startY: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

type ViewState = {
  zoom: number;
  panX: number;
  panY: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const clampView = (view: ViewState): ViewState => {
  const zoom = clamp(view.zoom, 1, 6);
  const maxPan = (zoom - 1) / 2;
  return {
    zoom,
    panX: clamp(view.panX, -maxPan, maxPan),
    panY: clamp(view.panY, -maxPan, maxPan)
  };
};

export function App() {
  const [thermogram, setThermogram] = useState<Thermogram>(() =>
    createSyntheticThermogram()
  );
  const [settings, setSettings] = useState<RadiometricSettings>(initialSettings);
  const [range, setRange] = useState<ThermalRange>({
    min: 18,
    max: 92,
    auto: true
  });
  const [palette, setPalette] = useState<PaletteId>("q19");
  const [showEmbedded, setShowEmbedded] = useState(true);
  const [tool, setTool] = useState<ToolMode>("box");
  const [boxes, setBoxes] = useState<AnalysisBox[]>(initialBoxes);
  const [points, setPoints] = useState<AnalysisPoint[]>(initialPoints);
  const [selectedId, setSelectedId] = useState("zone-a");
  const [draft, setDraft] = useState<DraftBox | null>(null);
  const [importState, setImportState] = useState("Pret");
  const [aiSummary, setAiSummary] = useState("");
  const [aiState, setAiState] = useState("Synthese locale");

  const correctedValues = useMemo(
    () =>
      applyRadiometricCorrection(
        thermogram.values,
        settings,
        thermogram.radiometricDefaults
      ),
    [thermogram, settings]
  );

  const stats = useMemo(() => valueStats(correctedValues), [correctedValues]);

  const effectiveRange = useMemo<ThermalRange>(() => {
    if (!range.auto) return range;
    const padding = Math.max(2, (stats.max - stats.min) * 0.04);
    return {
      min: Math.floor((stats.min - padding) * 10) / 10,
      max: Math.ceil((stats.max + padding) * 10) / 10,
      auto: true
    };
  }, [range, stats]);

  const metrics = useMemo<ZoneMetrics[]>(() => {
    const zoneMetrics = boxes.map((box) =>
      analyzeBox(thermogram, correctedValues, box, settings)
    );
    const pointMetrics = points.map((point) =>
      analyzePoint(thermogram, correctedValues, point, settings)
    );
    return [...zoneMetrics, ...pointMetrics];
  }, [boxes, correctedValues, points, settings, thermogram]);

  const selectedMetric = metrics.find((metric) => metric.id === selectedId) ?? metrics[0];

  useEffect(() => {
    setAiSummary(
      buildLocalAiSummary({
        fileName: thermogram.file?.name ?? "demo",
        decodeMode: thermogram.decodeMode,
        confidence: thermogram.confidence,
        settings,
        metrics
      })
    );
  }, [metrics, settings, thermogram]);

  const nextBoxLabel = () => `Zone ${String.fromCharCode(65 + boxes.length)}`;
  const nextPointLabel = () => `P${points.length + 1}`;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setImportState("Import en cours");
    try {
      const decoded = await decodeThermogramFile(file);
      setThermogram(decoded);
      setShowEmbedded(Boolean(decoded.visualUrl));
      if (decoded.radiometricDefaults) {
        setSettings((current) => ({
          ...current,
          ...decoded.radiometricDefaults
        }));
      }
      setBoxes([]);
      setPoints([]);
      setSelectedId("");
      setRange((current) => ({ ...current, auto: true }));
      setImportState("Import termine");
    } catch (error) {
      console.error(error);
      setImportState("Import impossible");
    }
  };

  // Image of the scene for the vision endpoint: prefer the embedded thermal
  // preview (the real captured scene, best for recognising equipment), and
  // fall back to a rendered heatmap when the file carries no preview.
  const sceneImage = (): string | undefined =>
    thermogram.visualUrl ??
    renderHeatmapDataUrl(thermogram, correctedValues, palette, effectiveRange);

  const payload = (
    metricsOverride?: ZoneMetrics[],
    withImage = false
  ): AiRequestPayload => ({
    fileName: thermogram.file?.name ?? "demo",
    decodeMode: thermogram.decodeMode,
    confidence: thermogram.confidence,
    settings,
    metrics: metricsOverride ?? metrics,
    image: withImage ? sceneImage() : undefined
  });

  const runAiSummary = async (data: AiRequestPayload) => {
    setAiState("Analyse en cours");
    try {
      const response = await fetch("/api/analyse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error("AI endpoint unavailable");
      const result = (await response.json()) as { summary?: string; mode?: string };
      setAiSummary(result.summary ?? buildLocalAiSummary(data));
      setAiState(
        result.mode === "workers-ai-vision"
          ? "Workers AI vision"
          : result.mode === "workers-ai"
            ? "Workers AI"
            : "Synthese locale"
      );
    } catch {
      setAiSummary(buildLocalAiSummary(data));
      setAiState("Synthese locale");
    }
  };

  const requestAiSummary = () => runAiSummary(payload(undefined, true));

  // One-click: auto-detect the hottest regions, place them as points, then ask
  // the vision endpoint to identify the equipment and propose an analysis.
  const runAutoAnalysis = async () => {
    const detected = detectHotspots(thermogram, correctedValues, settings);
    setPoints(detected);
    setBoxes([]);
    setSelectedId(detected[0]?.id ?? "");
    setShowEmbedded(false);
    const detectedMetrics = detected.map((point) =>
      analyzePoint(thermogram, correctedValues, point, settings)
    );
    await runAiSummary(payload(detectedMetrics, true));
  };

  const addPoint = (x: number, y: number) => {
    const point: AnalysisPoint = {
      id: `pt-${Date.now()}`,
      label: nextPointLabel(),
      x,
      y
    };
    setPoints((current) => [...current, point]);
    setSelectedId(point.id);
  };

  const completeDraft = () => {
    if (!draft || draft.width < 0.025 || draft.height < 0.025) {
      setDraft(null);
      return;
    }
    const box: AnalysisBox = {
      id: `zone-${Date.now()}`,
      label: nextBoxLabel(),
      x: draft.x,
      y: draft.y,
      width: draft.width,
      height: draft.height
    };
    setBoxes((current) => [...current, box]);
    setSelectedId(box.id);
    setDraft(null);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setBoxes((current) => current.filter((box) => box.id !== selectedId));
    setPoints((current) => current.filter((point) => point.id !== selectedId));
    setSelectedId("");
  };

  const updateSetting = (key: keyof RadiometricSettings, value: number) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="app-shell">
      <aside className="tool-rail" aria-label="Outils">
        <div className="brand-mark">
          <Activity size={22} />
        </div>
        <label className="icon-button primary" title="Importer">
          <FileUp size={20} />
          <input
            className="file-input"
            type="file"
            accept=".bmt,.BMT,.csv,.txt,.png,.jpg,.jpeg,.webp"
            onChange={(event) => handleFile(event.target.files?.[0])}
          />
        </label>
        <button
          className={tool === "box" ? "icon-button active" : "icon-button"}
          onClick={() => setTool("box")}
          title="Box"
        >
          <BoxSelect size={20} />
        </button>
        <button
          className={tool === "point" ? "icon-button active" : "icon-button"}
          onClick={() => setTool("point")}
          title="Point"
        >
          <CircleDot size={20} />
        </button>
        <button
          className={tool === "pan" ? "icon-button active" : "icon-button"}
          onClick={() => setTool("pan")}
          title="Select"
        >
          <MousePointer2 size={20} />
        </button>
        <button
          className="icon-button"
          onClick={() =>
            exportAnnotatedPng(
              thermogram,
              correctedValues,
              palette,
              effectiveRange,
              boxes,
              points,
              metrics,
              showEmbedded
            )
          }
          title="Exporter"
        >
          <Download size={20} />
        </button>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <h1>ThermoQ19</h1>
            <div className="status-line">
              <span>{thermogram.file?.name ?? "thermogramme-demo.bmt"}</span>
              <span>{decodeModeLabel(thermogram.decodeMode)}</span>
              <span>{Math.round(thermogram.confidence * 100)}%</span>
              <span>{importState}</span>
            </div>
          </div>
          <div className="topbar-actions">
            {thermogram.visualUrl && (
              <div className="segmented" aria-label="Affichage">
                <button
                  className={showEmbedded ? "selected" : ""}
                  onClick={() => setShowEmbedded(true)}
                  title="Afficher l'apercu thermique embarque du fichier"
                >
                  <Image size={15} />
                  Apercu
                </button>
                <button
                  className={!showEmbedded ? "selected" : ""}
                  onClick={() => setShowEmbedded(false)}
                  title="Afficher la matrice coloree par la palette"
                >
                  <Image size={15} />
                  Palette
                </button>
              </div>
            )}
            <SegmentedPalette
              palette={palette}
              onChange={(next) => {
                setPalette(next);
                setShowEmbedded(false);
              }}
            />
            <button className="command-button ghost" onClick={runAutoAnalysis}>
              <ScanSearch size={17} />
              Analyse auto
            </button>
            <button className="command-button" onClick={requestAiSummary}>
              <Wand2 size={17} />
              Synthese
            </button>
          </div>
        </header>

        <section className="analysis-layout">
          <section className="canvas-column">
            <ThermalCanvas
              thermogram={thermogram}
              values={correctedValues}
              range={effectiveRange}
              palette={palette}
              showEmbedded={showEmbedded}
              tool={tool}
              boxes={boxes}
              points={points}
              metrics={metrics}
              selectedId={selectedId}
              draft={draft}
              setSelectedId={setSelectedId}
              setDraft={setDraft}
              addPoint={addPoint}
              completeDraft={completeDraft}
            />
            <BottomStrip
              thermogram={thermogram}
              stats={stats}
              range={effectiveRange}
              tool={tool}
            />
          </section>

          <aside className="inspector">
            <PanelTitle icon={<SlidersHorizontal size={17} />} label="Reglages" />
            <div className="control-stack">
              <RangeControl
                label="Emissivite"
                value={settings.emissivity}
                min={0.1}
                max={1}
                step={0.01}
                format={(value) => value.toFixed(2)}
                onChange={(value) => updateSetting("emissivity", value)}
              />
              <NumberControl
                label="Temperature reflechie"
                value={settings.reflectedTempC}
                suffix="°C"
                onChange={(value) => updateSetting("reflectedTempC", value)}
              />
              <NumberControl
                label="T ambiante"
                value={settings.ambientTempC}
                suffix="°C"
                onChange={(value) => updateSetting("ambientTempC", value)}
              />
              <NumberControl
                label="Distance"
                value={settings.distanceM}
                suffix="m"
                step={0.1}
                onChange={(value) => updateSetting("distanceM", value)}
              />
              <NumberControl
                label="Humidite"
                value={settings.humidityPct}
                suffix="%"
                onChange={(value) => updateSetting("humidityPct", value)}
              />
            </div>

            <div className="range-panel">
              <div className="range-header">
                <span>Plage</span>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={range.auto}
                    onChange={(event) =>
                      setRange((current) => ({
                        ...current,
                        auto: event.target.checked
                      }))
                    }
                  />
                  Auto
                </label>
              </div>
              <div className="range-fields">
                <NumberControl
                  label="Min"
                  value={range.auto ? effectiveRange.min : range.min}
                  suffix="°C"
                  disabled={range.auto}
                  onChange={(value) =>
                    setRange((current) => ({ ...current, min: value }))
                  }
                />
                <NumberControl
                  label="Max"
                  value={range.auto ? effectiveRange.max : range.max}
                  suffix="°C"
                  disabled={range.auto}
                  onChange={(value) =>
                    setRange((current) => ({ ...current, max: value }))
                  }
                />
              </div>
            </div>

            <PanelTitle icon={<Layers size={17} />} label="Mesures" />
            <div className="measure-list">
              {metrics.length === 0 ? (
                <div className="empty-state">Aucune mesure</div>
              ) : (
                metrics.map((metric) => (
                  <button
                    key={metric.id}
                    className={
                      metric.id === selectedId
                        ? `measure-row selected ${metric.severity}`
                        : `measure-row ${metric.severity}`
                    }
                    onClick={() => setSelectedId(metric.id)}
                  >
                    <span>{metric.label}</span>
                    <strong>{formatTemp(metric.max)}</strong>
                  </button>
                ))
              )}
            </div>

            {selectedMetric && (
              <SelectedMeasure metric={selectedMetric} onDelete={deleteSelected} />
            )}

            <div className="ai-panel">
              <div className="ai-panel-header">
                <Sparkles size={17} />
                <span>{aiState}</span>
              </div>
              <p>{aiSummary}</p>
            </div>

            <div className="decode-note">
              <BadgeAlert size={16} />
              <span>{thermogram.note}</span>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
}

function ThermalCanvas({
  thermogram,
  values,
  range,
  palette,
  showEmbedded,
  tool,
  boxes,
  points,
  metrics,
  selectedId,
  draft,
  setSelectedId,
  setDraft,
  addPoint,
  completeDraft
}: {
  thermogram: Thermogram;
  values: Float32Array;
  range: ThermalRange;
  palette: PaletteId;
  showEmbedded: boolean;
  tool: ToolMode;
  boxes: AnalysisBox[];
  points: AnalysisPoint[];
  metrics: ZoneMetrics[];
  selectedId: string;
  draft: DraftBox | null;
  setSelectedId: (id: string) => void;
  setDraft: (draft: DraftBox | null) => void;
  addPoint: (x: number, y: number) => void;
  completeDraft: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const panRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [view, setView] = useState<ViewState>({ zoom: 1, panX: 0, panY: 0 });
  const metricById = useMemo(
    () => new Map(metrics.map((metric) => [metric.id, metric])),
    [metrics]
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    drawHeatmap(canvasRef.current, thermogram, values, palette, range);
  }, [palette, range, thermogram, values]);

  useEffect(() => {
    setView({ zoom: 1, panX: 0, panY: 0 });
  }, [thermogram]);

  const pointFromEvent = (event: PointerEvent<HTMLDivElement>) => {
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
    };
  };

  const zoomAt = (nextZoom: number, anchorX = 0.5, anchorY = 0.5) => {
    setView((current) => {
      const currentZoom = current.zoom;
      const imageX = 0.5 + (anchorX - 0.5 - current.panX) / currentZoom;
      const imageY = 0.5 + (anchorY - 0.5 - current.panY) / currentZoom;
      return clampView({
        zoom: nextZoom,
        panX: anchorX - 0.5 - nextZoom * (imageX - 0.5),
        panY: anchorY - 0.5 - nextZoom * (imageY - 0.5)
      });
    });
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const anchorX = (event.clientX - rect.left) / rect.width;
    const anchorY = (event.clientY - rect.top) / rect.height;
    const factor = event.deltaY < 0 ? 1.16 : 0.86;
    zoomAt(view.zoom * factor, anchorX, anchorY);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (tool === "pan") {
      if (view.zoom <= 1) {
        setSelectedId("");
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      panRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        panX: view.panX,
        panY: view.panY
      };
      return;
    }

    const point = pointFromEvent(event);
    if (tool === "point") {
      addPoint(point.x, point.y);
      return;
    }
    if (tool === "box") {
      event.currentTarget.setPointerCapture(event.pointerId);
      setDraft({
        startX: point.x,
        startY: point.y,
        x: point.x,
        y: point.y,
        width: 0,
        height: 0
      });
      return;
    }
    setSelectedId("");
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (tool === "pan" && panRef.current?.pointerId === event.pointerId) {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      setView(
        clampView({
          zoom: view.zoom,
          panX: panRef.current.panX + (event.clientX - panRef.current.startX) / rect.width,
          panY:
            panRef.current.panY + (event.clientY - panRef.current.startY) / rect.height
        })
      );
      return;
    }

    if (!draft || tool !== "box") return;
    const point = pointFromEvent(event);
    setDraft({
      ...draft,
      x: Math.min(draft.startX, point.x),
      y: Math.min(draft.startY, point.y),
      width: Math.abs(point.x - draft.startX),
      height: Math.abs(point.y - draft.startY)
    });
  };

  const stopGesture = (event?: PointerEvent<HTMLDivElement>) => {
    if (event && panRef.current?.pointerId === event.pointerId) {
      panRef.current = null;
    }
    completeDraft();
  };

  return (
    <div className="canvas-shell">
      <div
        ref={stageRef}
        className="thermal-stage"
        style={{
          aspectRatio: `${thermogram.width} / ${thermogram.height}`
        }}
        onWheel={handleWheel}
      >
        <div
          ref={layerRef}
          className="thermal-layer"
          style={{
            width: `${view.zoom * 100}%`,
            height: `${view.zoom * 100}%`,
            left: `${50 + view.panX * 100}%`,
            top: `${50 + view.panY * 100}%`
          }}
        >
          {thermogram.visualUrl && showEmbedded && (
            <img
              className="thermal-visual"
              src={thermogram.visualUrl}
              alt="Rendu thermique embarque"
            />
          )}
          <canvas ref={canvasRef} className="thermal-canvas" />
        </div>
        <div
          className={`thermal-overlay ${tool === "pan" && view.zoom > 1 ? "panning" : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopGesture}
          onPointerCancel={(event) => {
            if (panRef.current?.pointerId === event.pointerId) {
              panRef.current = null;
            }
            setDraft(null);
          }}
        >
          <div
            className="thermal-annotations"
            style={{
              width: `${view.zoom * 100}%`,
              height: `${view.zoom * 100}%`,
              left: `${50 + view.panX * 100}%`,
              top: `${50 + view.panY * 100}%`
            }}
          >
            {boxes.map((box) => {
              const metric = metricById.get(box.id);
              const selected = selectedId === box.id;
              return (
                <div key={box.id}>
                  <div
                    className={`analysis-box ${metric?.severity ?? "normal"} ${
                      selected ? "selected" : ""
                    }`}
                    style={{
                      left: `${box.x * 100}%`,
                      top: `${box.y * 100}%`,
                      width: `${box.width * 100}%`,
                      height: `${box.height * 100}%`
                    }}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      setSelectedId(box.id);
                    }}
                  />
                  <div
                    className="box-label"
                    style={{
                      left: `${(box.x + 0.008) * 100}%`,
                      top: `${Math.max(0.022, box.y - 0.012) * 100}%`
                    }}
                  >
                    {box.label} {metric ? formatTemp(metric.max) : ""}
                  </div>
                  {metric?.hotspot && (
                    <div
                      className="hotspot"
                      style={{
                        left: `${metric.hotspot.x * 100}%`,
                        top: `${metric.hotspot.y * 100}%`
                      }}
                    />
                  )}
                </div>
              );
            })}
            {points.map((point) => {
              const metric = metricById.get(point.id);
              return (
                <div
                  key={point.id}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    setSelectedId(point.id);
                  }}
                >
                  <div
                    className={
                      selectedId === point.id
                        ? "analysis-point selected"
                        : "analysis-point"
                    }
                    style={{
                      left: `${point.x * 100}%`,
                      top: `${point.y * 100}%`
                    }}
                  />
                  <div
                    className="point-label"
                    style={{
                      left: `${Math.min(0.92, point.x + 0.015) * 100}%`,
                      top: `${Math.max(0.03, point.y - 0.015) * 100}%`
                    }}
                  >
                    {point.label} {metric ? formatTemp(metric.max) : ""}
                  </div>
                </div>
              );
            })}
            {draft && (
              <div
                style={{
                  left: `${draft.x * 100}%`,
                  top: `${draft.y * 100}%`,
                  width: `${draft.width * 100}%`,
                  height: `${draft.height * 100}%`
                }}
                className="draft-box"
              />
            )}
          </div>
        </div>
        <div className="zoom-bar" aria-label="Zoom">
          <button
            type="button"
            onClick={() => zoomAt(view.zoom / 1.35)}
            title="Zoom arriere"
            aria-label="Zoom arriere"
          >
            <ZoomOut size={15} />
          </button>
          <span>{Math.round(view.zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => zoomAt(view.zoom * 1.35)}
            title="Zoom avant"
            aria-label="Zoom avant"
          >
            <ZoomIn size={15} />
          </button>
          <button
            type="button"
            onClick={() => setView({ zoom: 1, panX: 0, panY: 0 })}
            title="Ajuster"
            aria-label="Ajuster le zoom"
          >
            <Maximize2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SegmentedPalette({
  palette,
  onChange
}: {
  palette: PaletteId;
  onChange: (palette: PaletteId) => void;
}) {
  return (
    <div className="segmented" aria-label="Palette">
      {paletteOptions.map((option) => (
        <button
          key={option.id}
          className={palette === option.id ? "selected" : ""}
          onClick={() => onChange(option.id)}
        >
          <Image size={15} />
          {option.label}
        </button>
      ))}
    </div>
  );
}

function PanelTitle({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="panel-title">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function RangeControl({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="range-control">
      <span>
        {label}
        <strong>{format(value)}</strong>
      </span>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function NumberControl({
  label,
  value,
  suffix,
  step = 1,
  disabled,
  onChange
}: {
  label: string;
  value: number;
  suffix: string;
  step?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <label className="number-control">
      <span>{label}</span>
      <div>
        <input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          step={step}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <b>{suffix}</b>
      </div>
    </label>
  );
}

function SelectedMeasure({
  metric,
  onDelete
}: {
  metric: ZoneMetrics;
  onDelete: () => void;
}) {
  return (
    <section className={`selected-measure ${metric.severity}`}>
      <div className="selected-header">
        <span>{metric.label}</span>
        <button className="icon-mini" onClick={onDelete} title="Supprimer">
          <Trash2 size={15} />
        </button>
      </div>
      <div className="metric-grid">
        <Metric label="Min" value={formatTemp(metric.min)} />
        <Metric label="Max" value={formatTemp(metric.max)} />
        <Metric label="Moyenne" value={formatTemp(metric.avg)} />
        <Metric label="Delta" value={formatTemp(metric.deltaAmbient)} />
      </div>
      <div className="severity-line">{severityLabel(metric.severity)}</div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function BottomStrip({
  thermogram,
  stats,
  range,
  tool
}: {
  thermogram: Thermogram;
  stats: { min: number; max: number; avg: number };
  range: ThermalRange;
  tool: ToolMode;
}) {
  return (
    <div className="bottom-strip">
      <div className="strip-item">
        <span>Resolution</span>
        <strong>
          {thermogram.width} x {thermogram.height}
        </strong>
      </div>
      <div className="strip-item">
        <span>Min</span>
        <strong>{formatTemp(stats.min)}</strong>
      </div>
      <div className="strip-item">
        <span>Max</span>
        <strong>{formatTemp(stats.max)}</strong>
      </div>
      <div className="strip-item">
        <span>Moyenne</span>
        <strong>{formatTemp(stats.avg)}</strong>
      </div>
      <div className="strip-item">
        <span>Plage</span>
        <strong>
          {range.min.toFixed(1)} - {range.max.toFixed(1)} °C
        </strong>
      </div>
      <div className="strip-item">
        <span>Outil</span>
        <strong>{toolLabels[tool]}</strong>
      </div>
    </div>
  );
}
